// Free public tool — Google Review Response Generator
// Paste a review → 3 on-brand reply drafts with sentiment tagging.
// No auth required. IP rate-limited at the backend (20/hr).
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Star, Copy, Loader2, Sparkles, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import PublicHeader from "@/components/layout/PublicHeader";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const TONE_COLORS = {
  warm_thanks:           "bg-emerald-100 text-emerald-700",
  earnest_apology:       "bg-rose-100 text-rose-700",
  professional_correction: "bg-sky-100 text-sky-700",
  playful_appreciation:  "bg-violet-100 text-violet-700",
  fact_based:            "bg-amber-100 text-amber-700",
};

export default function ReviewResponder() {
  const [reviewText, setReviewText] = useState("");
  const [stars, setStars] = useState(3);
  const [reviewerName, setReviewerName] = useState("");
  const [propertyLabel, setPropertyLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (reviewText.trim().length < 10) { toast.error("Paste the review (≥ 10 chars)"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await fetch(`${API}/api/tools/review-responder/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          review_text: reviewText, star_rating: stars,
          reviewer_name: reviewerName || null,
          property_label: propertyLabel || null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data?.detail || `Failed (HTTP ${r.status})`);
        return;
      }
      setResult(data);
      toast.success(`Got ${data.drafts?.length || 0} reply drafts (${data.sentiment})`);
    } catch (e) { toast.error(e?.message || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt || "").then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed"),
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="review-responder-page">
      <PublicHeader light={true}/>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-violet-700">Free tool</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 leading-tight">Smart Google Review Response Generator</h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl leading-relaxed">Paste a Google review, get 3 on-brand reply variants in 10 seconds. Built for Canadian landlords &amp; property managers. No signup required.</p>
        </div>

        <Card className="p-5 mt-6">
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 block">Paste the review</label>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={5}
                data-testid="rr-review"
                placeholder="e.g. Maintenance never responded for 3 weeks…"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-violet-500"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 block">Star rating</label>
              <div className="flex gap-1" data-testid="rr-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => setStars(s)} data-testid={`rr-star-${s}`}
                    className={`p-1 ${s <= stars ? "text-amber-500" : "text-slate-300"} hover:scale-110 transition-transform`}>
                    <Star className="w-5 h-5" fill={s <= stars ? "currentColor" : "none"}/>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 block">Reviewer first name (optional)</label>
              <input value={reviewerName} onChange={(e) => setReviewerName(e.target.value)}
                data-testid="rr-name"
                placeholder="e.g. Jamie"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-violet-500"/>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 block">Property / building (optional)</label>
              <input value={propertyLabel} onChange={(e) => setPropertyLabel(e.target.value)}
                data-testid="rr-property"
                placeholder='e.g. "Jasper Ave Suite 12"'
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-violet-500"/>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={run} disabled={busy || reviewText.trim().length < 10} data-testid="rr-run"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
              {busy ? "Drafting…" : "Generate 3 replies"}
            </button>
          </div>
        </Card>

        {result && (
          <Card className="p-5 mt-4" data-testid="rr-result">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${
                result.sentiment === "positive" ? "bg-emerald-100 text-emerald-700" :
                result.sentiment === "negative" ? "bg-rose-100 text-rose-700" :
                                                  "bg-amber-100 text-amber-700"
              }`}>{result.sentiment} sentiment</span>
              {result.rationale && <span className="text-[11px] text-slate-500 italic">{result.rationale}</span>}
            </div>
            <div className="space-y-2" data-testid="rr-drafts">
              {(result.drafts || []).map((d, i) => (
                <div key={i} className="p-3 rounded-lg border border-slate-200 bg-white hover:border-violet-300 transition-colors" data-testid={`rr-draft-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${TONE_COLORS[d.tone] || "bg-slate-100 text-slate-700"}`}>{(d.tone || "").replace(/_/g, " ")}</span>
                    <button onClick={() => copy(d.text, `Draft ${i + 1}`)} data-testid={`rr-copy-${i}`}
                      className="text-[11px] px-2 py-0.5 rounded bg-white border border-slate-300 hover:border-violet-400 text-slate-700 font-bold inline-flex items-center gap-1">
                      <Copy className="w-3 h-3"/> Copy
                    </button>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{d.text}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="mt-8 text-xs text-slate-500 text-center max-w-xl mx-auto leading-relaxed flex items-center justify-center gap-1.5">
          <MessageSquare className="w-3 h-3"/>
          Free for everyone, no signup. Powered by the same Smart engine that runs
          Central Rentals&apos; messaging stack.
        </div>
      </div>
    </div>
  );
}
